/* eslint-disable react/destructuring-assignment */
import { NextPageContext } from 'next';
import Head from 'next/head';
import {
  ActivityCode,
  Content,
  ContentTypesCount,
  InlineResponse2001,
  InterestCode,
  SearchType,
} from 'pleinchamp-api-client';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchFetching, useSearchRouteQuery } from '@search/business/search.hooks';
import {
  activityInUrl,
  contentTypeInUrl,
  interestInUrl,
  SearchContentType,
  SearchQueryParams,
} from '@search/business/search.types';
import { getSearchContentTypes, searchContent, SearchParams } from '@search/business/search.utils';
import { SearchLayout } from '@search/components/SearchLayout/SearchLayout.component';
import { Constants } from '@utils/Constants';
import { ExtendedPagination } from '@utils/pagination';
import { defaultNamespaces } from '@utils/strings';
import { castUrlParamToString, transformUrlParamToEnum, transformUrlParamToEnumArray } from '@utils/url';
import { GeneralContext } from 'features/business/app.contexts';
import { PlcNextPage, PlcPageContextProps } from 'features/business/app.types';

export type SearchPageProps = SearchQueryParams & { searchResults: InlineResponse2001 };
interface ContentProps {
  results: Content[];
  pagination: ExtendedPagination;
  counts: ContentTypesCount;
}

// Query cannot be part of this context because it is used by SearchForm which is in the header and thus ouotside of this context...
type ContextProps = PlcPageContextProps<Omit<SearchPageProps, 'query' | 'searchResults'> & ContentProps> & {
  nextResults: {
    fetchNextResults?: () => void;
    isValidating: boolean;
  };
};

export const SearchContext = createContext<ContextProps>({
  activities: { isValidating: false },
  contentType: { isValidating: false },
  counts: { isValidating: false },
  interests: { isValidating: false },
  nextResults: { isValidating: false },
  pagination: { isValidating: false },
  results: { isValidating: false },
});

export function fetchResults(params: Omit<SearchParams, 'contentTypes'> & { contentType?: SearchContentType | null }) {
  const { activities, contentType, interests, offset = 0, query, searchType = SearchType.Prioritize, limit } = params;
  return searchContent({
    activities,
    contentTypes: getSearchContentTypes(contentType),
    interests,
    limit,
    offset,
    query,
    searchType,
  });
}

const SearchPageWithQuery: PlcNextPage<Partial<SearchPageProps & { resultPerPage: number }>> = props => {
  const resultPerPage = props.resultPerPage ? props.resultPerPage : Constants.SEARCH_RESULT_PER_PAGE[0];
  const defaultOffset = useMemo(() => (props.pageNb || 0) * resultPerPage, [props.pageNb]);
  const [offset, setOffset] = useState(defaultOffset);
  const { searchQuery, setSearchQuery } = useContext(GeneralContext);

  useEffect(() => {
    setSearchQuery?.(props.query || '');
  }, []);

  // Need to redeclare a query object to make sure to set offset to 0 in case of query change (otherwise we risk a 404)
  const [query, setQuery] = useState(searchQuery);
  useEffect(() => {
    setOffset(defaultOffset);
    setQuery(searchQuery);
  }, [searchQuery]);

  const [activities, setActivities] = useState(props.activities || []);
  const [interests, setInterests] = useState(props.interests || []);
  const [contentType, setContentType] = useState(props.contentType || SearchContentType.All);

  const { fetchNextResults, pagination, isNextResultsValidating, data, results, searchResults } = useSearchFetching({
    activities,
    contentType,
    defaultOffset,
    initialData: props.searchResults,
    interests,
    limit: resultPerPage,
    offset,
    query,
    setOffset,
  });

  useSearchRouteQuery(interests, activities, contentType, searchQuery, props.pageNb);

  const setActivitiesFilter: typeof setActivities = newActivities => {
    setOffset(defaultOffset);
    setActivities(newActivities);
  };

  const setInterestsFilter: typeof setInterests = newInterests => {
    setOffset(defaultOffset);
    setInterests(newInterests);
  };

  const setContentTypeFilter: typeof setContentType = newContentType => {
    setOffset(defaultOffset);
    setContentType(newContentType);
  };

  const { isValidating } = searchResults;

  return (
    <SearchContext.Provider
      value={{
        activities: { data: activities, isValidating: false, setData: setActivitiesFilter },
        contentType: { data: contentType, isValidating: false, setData: setContentTypeFilter },
        counts: { data: data?.contentTypesCount, isValidating },
        interests: { data: interests, isValidating: false, setData: setInterestsFilter },
        nextResults: { fetchNextResults, isValidating: isNextResultsValidating },
        pagination: { data: pagination, isValidating },
        results: { data: results, isValidating },
      }}
    >
      <Head>
        <meta content="7200" httpEquiv="refresh" />
      </Head>
      <SearchLayout />
    </SearchContext.Provider>
  );
};

export function getServerSideProps({ query, req }: NextPageContext) {
  // eslint-disable-next-line @typescript-eslint/camelcase
  const { mots_cles, type_de_contenu, activites, centres_d_interets, page } = query;
  const isServer = !!req;
  const userAgent = isServer && req ? req.headers['user-agent'] : navigator.userAgent;
  const isMobile = userAgent ? /(iPad|iPhone|Android|Mobile)/i.test(userAgent) : false;
  const resultPerPage = isMobile ? Constants.SEARCH_RESULT_PER_PAGE[0] : Constants.SEARCH_RESULT_PER_PAGE[1];

  const searchQuery = castUrlParamToString(mots_cles);

  const contentTypeIndex = Object.values(contentTypeInUrl).findIndex(
    ct => ct === castUrlParamToString(type_de_contenu)
  );
  const contentType =
    contentTypeIndex >= 0
      ? transformUrlParamToEnum<SearchContentType>(Object.keys(contentTypeInUrl)[contentTypeIndex], SearchContentType)
      : null;

  const activitiesIndexes = castUrlParamToString(activites)
    ?.split(',')
    .map(v => Object.values(activityInUrl).findIndex(a => a === v));
  const activities = activitiesIndexes
    ? transformUrlParamToEnumArray<ActivityCode>(
        activitiesIndexes.map(v => Object.keys(activityInUrl)[v]),
        ActivityCode
      )
    : [];

  const interestsIndexes = castUrlParamToString(centres_d_interets)
    ?.split(',')
    .map(v => Object.values(interestInUrl).findIndex(i => i === v));
  const interests = interestsIndexes
    ? transformUrlParamToEnumArray<InterestCode>(
        interestsIndexes.map(v => Object.keys(interestInUrl)[v]),
        InterestCode
      )
    : [];

  const pageNb = page ? Number(castUrlParamToString(page)) : null;

  const pageDefaultState = {
    activities,
    contentType,
    interests,
    namespacesRequired: defaultNamespaces,
    pageNb,
    query: searchQuery ?? null,
    resultPerPage,
  };

  return fetchResults({
    activities,
    contentType,
    interests,
    limit: resultPerPage,
    offset: (pageNb || 0) * resultPerPage,
    query: searchQuery,
  })
    .then(searchResults => ({ props: { ...pageDefaultState, searchResults } }))
    .catch(() => ({ props: pageDefaultState }));
}

export default SearchPageWithQuery;
